/**
 * ContactCockpit — protocol-driven reporting workspace for the
 * standard period of patient contact (parallel to Code 99 Mode but
 * for ANY call, not just cardiac arrest).
 *
 * Mounts inside the Procedures tab when the EMT taps 'Open Cockpit'.
 * Replaces the standard Procedures form with a three-zone command
 * surface modeled on Code 99:
 *
 *   ZONE 1 (top)    Status strip — running clocks for time on scene
 *                   and time since patient contact, plus the active
 *                   protocol header if one matched the chief
 *                   complaint / impression.
 *
 *   ZONE 2 (left)   Quick-tap palette — six big hardware buttons
 *                   for the most common interventions: Medication,
 *                   Procedure, Vital Set, 12-Lead, IV / IO, Note.
 *                   Each opens a tight inline form; submit auto-
 *                   stamps the time and writes the structured
 *                   record to the appropriate Dexie table.
 *
 *   ZONE 3 (right)  Live event log — reverse-chronological list of
 *                   every entry made during this contact, each
 *                   timestamped to the second.
 *
 * The AppShell rail stays accessible (the cockpit is rendered
 * inside WorkflowBody, not as a fullscreen overlay) so the EMT can
 * still tap to Patient / Vitals / Disposition mid-call to add
 * upstream info as it comes in.
 *
 * Cockpit visual language: gunmetal canvas, brushed-metal rail
 * buttons, white inset displays for inputs. Same .rail-button +
 * .rail-cockpit styling as the workflow rail.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Clock, Pill, Syringe, Activity, FileText, Stethoscope, X,
  Plus, ChevronDown, ChevronRight, Sparkles, Heart, Droplet,
} from 'lucide-react';
import type { EPCR, MedicationGiven, Procedure, VitalSet } from '@/types';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import { activeProtocol, activeProtocols } from '@/lib/care-matrix';
import { minScopeForDrug, minScopeForProcedure, scopeBlockReason } from '@/lib/scope';
import { cn } from '@/lib/cn';
import { CareMatrix } from './CareMatrix';
import { Undo2, Edit3 } from 'lucide-react';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
  onClose: () => void;
}

type EntryKind = 'med' | 'procedure' | 'vital' | 'twelve_lead' | 'iv' | 'note';

interface LogRow {
  id: string;
  kind: EntryKind;
  timestamp: string;
  label: string;
  detail?: string;
}

export function ContactCockpit({ epcr, onUpdate, onClose }: Props) {
  const { user } = useAuthStore();
  const [openEntry, setOpenEntry] = useState<EntryKind | null>(null);
  const [log, setLog] = useState<LogRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const protocol = useMemo(() => activeProtocol(epcr), [epcr]);
  const allMatchingProtocols = useMemo(() => activeProtocols(epcr), [epcr]);
  // Per-protocol expansion state. Default all collapsed; the EMT
  // taps a protocol header to drill into its step pipeline. Storing
  // the EXPANDED set keeps the UI honest if a new protocol auto-
  // matches mid-call (it stays collapsed, doesn't auto-jump open).
  const [expandedProtocols, setExpandedProtocols] = useState<Set<string>>(new Set());

  function toggleProtocolExpanded(id: string) {
    setExpandedProtocols((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Pull existing rows for THIS ePCR so the event log shows real
  // history (not just things logged in this session).
  useEffect(() => {
    let cancel = false;
    void (async () => {
      const [meds, procs, vitals] = await Promise.all([
        db.medications_given.where('epcr_id').equals(epcr.id).toArray(),
        db.procedures.where('epcr_id').equals(epcr.id).toArray(),
        db.vital_sets.where('epcr_id').equals(epcr.id).toArray(),
      ]);
      if (cancel) return;
      const rows: LogRow[] = [
        ...meds.map((m) => ({
          id: m.id, kind: 'med' as EntryKind, timestamp: m.timestamp,
          label: `${m.drug_name} ${m.dose} ${m.unit} ${m.route}`,
          detail: m.patient_response ?? undefined,
        })),
        ...procs.map((p) => ({
          id: p.id, kind: 'procedure' as EntryKind, timestamp: p.timestamp,
          label: p.procedure_type,
          detail: p.body_site ?? undefined,
        })),
        ...vitals.map((v) => ({
          id: v.id, kind: 'vital' as EntryKind, timestamp: v.timestamp,
          label: 'Vital set',
          detail: vitalSummary(v),
        })),
      ];
      setLog(rows.sort((a, b) => b.timestamp.localeCompare(a.timestamp)));
    })();
    return () => { cancel = true; };
  }, [epcr.id]);

  function pushRow(row: LogRow) {
    setLog((prev) => [row, ...prev]);
  }

  /**
   * Edit the timestamp on a logged entry. State / federal mandates
   * require accurate intervention timestamps and tap-time may not
   * reflect actual moment of administration; this prompt lets the
   * EMT correct it without unrolling and re-recording.
   */
  async function editRowTime(row: LogRow) {
    const cur = new Date(row.timestamp);
    const pad = (n: number) => String(n).padStart(2, '0');
    const initial = `${pad(cur.getHours())}:${pad(cur.getMinutes())}:${pad(cur.getSeconds())}`;
    const input = window.prompt(
      `Correct timestamp (HH:MM or HH:MM:SS, 24-hour). Currently ${initial}.`,
      initial,
    );
    if (!input) return;
    const m = input.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) {
      setError(`Invalid time format "${input}". Use HH:MM or HH:MM:SS (24-hour).`);
      return;
    }
    const next = new Date(cur);
    next.setHours(Number(m[1]), Number(m[2]), Number(m[3] ?? '0'), 0);
    const ts = next.toISOString();
    try {
      switch (row.kind) {
        case 'med':
          await db.medications_given.update(row.id, { timestamp: ts, _dirty: true, _last_modified: new Date().toISOString() } as never);
          enqueueSync('medications_given', row.id, 'update', { id: row.id, timestamp: ts });
          break;
        case 'procedure':
        case 'twelve_lead':
        case 'iv':
          await db.procedures.update(row.id, { timestamp: ts, _dirty: true, _last_modified: new Date().toISOString() } as never);
          enqueueSync('procedures', row.id, 'update', { id: row.id, timestamp: ts });
          break;
        case 'vital':
          await db.vital_sets.update(row.id, { timestamp: ts, _dirty: true, _last_modified: new Date().toISOString() } as never);
          enqueueSync('vital_sets', row.id, 'update', { id: row.id, timestamp: ts });
          break;
        case 'note':
          break;
      }
      setLog((prev) => prev.map((r) => r.id === row.id ? { ...r, timestamp: ts } : r));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update timestamp.');
    }
  }

  /**
   * Undo a logged entry — deletes the structured Dexie record and
   * removes it from the visible log. Critical for accidental taps:
   * an EMT who tapped Medication and dismissed must be able to back
   * out without leaving a phantom record. Notes are not stored in
   * Dexie (they're free-text additions to the narrative); for those
   * we only clear the visible log entry.
   */
  async function undoLogRow(row: LogRow) {
    try {
      switch (row.kind) {
        case 'med': {
          await db.medications_given.delete(row.id);
          enqueueSync('medications_given', row.id, 'delete', { id: row.id });
          break;
        }
        case 'procedure':
        case 'twelve_lead':
        case 'iv': {
          await db.procedures.delete(row.id);
          enqueueSync('procedures', row.id, 'delete', { id: row.id });
          break;
        }
        case 'vital': {
          await db.vital_sets.delete(row.id);
          enqueueSync('vital_sets', row.id, 'delete', { id: row.id });
          break;
        }
        case 'note':
          // Notes are narrative-only; no Dexie row to delete.
          break;
      }
      setLog((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to undo entry.');
    }
  }

  return (
    <section className="rail-cockpit border border-[var(--cockpit-edge-deep)] rounded-md overflow-hidden">
      {/* ZONE 1 — status strip */}
      <header className="flex items-center gap-3 border-b border-[var(--cockpit-edge-deep)] px-3 py-2 flex-wrap">
        <div className="flex h-8 w-8 items-center justify-center bg-blue-700 text-white rounded">
          <Heart size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)]">
            Patient Contact Cockpit
          </div>
          {protocol ? (
            <div className="text-sm font-bold text-[var(--cockpit-text-on-panel)] truncate">
              {protocol.name}
            </div>
          ) : (
            <div className="text-sm font-bold text-[var(--cockpit-text-on-panel)] truncate">
              {epcr.primary_impression ?? epcr.chief_complaint ?? 'Standard patient contact'}
            </div>
          )}
        </div>
        <Clocks />
        <button
          type="button"
          onClick={onClose}
          title="Close cockpit"
          aria-label="Close cockpit"
          className="rail-button no-bevel min-h-touch min-w-touch flex items-center justify-center px-2 py-2"
        >
          <X size={16} />
        </button>
      </header>

      {error && (
        <div className="border-b border-red-500 bg-red-900/40 text-red-100 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* PROTOCOL PIPELINES — every protocol the chief complaint /
          impression triggers renders as a collapsible header row.
          Default collapsed so the controls + palette stay above the
          fold; tap a header to drill into that protocol's step list.
          Expanding multiple at once is supported (a CHF + bronchospasm
          patient might be running on Respiratory + Anaphylaxis at the
          same time). */}
      {allMatchingProtocols.length > 0 && (
        <div className="border-b border-[var(--cockpit-edge-deep)] bg-[var(--cockpit-canvas)]">
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)] flex items-center gap-2">
            <Sparkles size={12} className="text-amber-400" />
            <span>
              {allMatchingProtocols.length === 1
                ? 'Active protocol'
                : `${allMatchingProtocols.length} active protocols`}
              {' · '}tap header to expand
            </span>
          </div>
          <ul className="space-y-1 px-2 pb-2">
            {allMatchingProtocols.map((proto) => {
              const isOpen = expandedProtocols.has(proto.id);
              return (
                <li key={proto.id} className="rail-button overflow-hidden p-0">
                  <button
                    type="button"
                    onClick={() => toggleProtocolExpanded(proto.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                    aria-expanded={isOpen}
                  >
                    <Sparkles size={14} className="text-amber-400 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-sm font-bold text-[var(--cockpit-text-on-panel)]">
                      {proto.name}
                    </span>
                    <span className="font-mono text-[10px] text-[var(--cockpit-text-on-panel-3)] shrink-0">
                      v{proto.version}
                    </span>
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  {isOpen && (
                    <div className="border-t border-[var(--cockpit-edge-deep)]">
                      <CareMatrix
                        epcr={epcr}
                        onUpdate={onUpdate}
                        protocolOverride={proto}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ZONES 2 + 3 — palette on left, event log on right */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-0">
        {/* ZONE 2 — quick-tap palette (off-protocol entries) */}
        <div className="border-r border-[var(--cockpit-edge-deep)] p-2 space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)] px-1 pt-1">
            {protocol ? 'Off-protocol entries · time stamps automatically' : 'Tap to log · time stamps automatically'}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <PaletteButton icon={Pill}        label="Medication"  onClick={() => setOpenEntry('med')} />
            <PaletteButton icon={Stethoscope} label="Procedure"   onClick={() => setOpenEntry('procedure')} />
            <PaletteButton icon={Activity}    label="Vital Set"   onClick={() => setOpenEntry('vital')} />
            <PaletteButton icon={Heart}       label="12-Lead"     onClick={() => setOpenEntry('twelve_lead')} />
            <PaletteButton icon={Syringe}     label="IV / IO"     onClick={() => setOpenEntry('iv')} />
            <PaletteButton icon={FileText}    label="Note"        onClick={() => setOpenEntry('note')} />
          </div>

          {/* Inline entry form for the selected kind */}
          {openEntry && (
            <EntryForm
              kind={openEntry}
              epcr={epcr}
              userId={user?.id ?? null}
              certLevel={user?.cert_level ?? 'EMT'}
              protocolId={protocol?.id ?? null}
              onSubmit={(row) => { pushRow(row); setOpenEntry(null); setError(null); }}
              onError={setError}
              onUpdate={onUpdate}
              onCancel={() => { setOpenEntry(null); setError(null); }}
            />
          )}
        </div>

        {/* ZONE 3 — event log */}
        <div className="p-2 max-h-[60vh] overflow-y-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-3)] px-1 pt-1 pb-1.5 sticky top-0 bg-[var(--cockpit-panel)]">
            Event log · {log.length} entries
          </div>
          {log.length === 0 ? (
            <p className="text-sm text-[var(--cockpit-text-on-panel-3)] italic px-1 py-3">
              No entries yet. Tap a button above to log your first action.
            </p>
          ) : (
            <ol className="space-y-1">
              {log.map((row) => (
                <LogRowDisplay
                  key={row.id}
                  row={row}
                  onUndo={() => undoLogRow(row)}
                  onEditTime={() => void editRowTime(row)}
                  locked={epcr.locked}
                />
              ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
 * Palette button
 * ================================================================ */

function PaletteButton({
  icon: Icon, label, onClick,
}: {
  icon: typeof Pill;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rail-button flex flex-col items-center justify-center gap-1 px-2 py-3 min-h-[68px]"
    >
      <Icon size={18} />
      <span className="text-[11px] font-bold leading-tight text-center">{label}</span>
    </button>
  );
}

/* ================================================================
 * Running clocks — time on scene + time since patient contact
 * ================================================================ */

function Clocks() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const time = new Date(now).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  return (
    <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded bg-black/40 border border-[var(--cockpit-edge-deep)]">
      <Clock size={12} className="text-[var(--cockpit-text-on-panel-2)]" />
      <span className="font-mono text-sm tabular-nums text-emerald-300">{time}</span>
    </div>
  );
}

/* ================================================================
 * EntryForm — switches by kind. Each variant does the minimum
 * needed to record a structured row.
 * ================================================================ */

function EntryForm(props: {
  kind: EntryKind;
  epcr: EPCR;
  userId: string | null;
  certLevel: string;
  protocolId: string | null;
  onSubmit: (row: LogRow) => void;
  onError: (msg: string) => void;
  onUpdate: (u: Partial<EPCR>) => void;
  onCancel: () => void;
}) {
  switch (props.kind) {
    case 'med':         return <MedForm {...props} />;
    case 'procedure':   return <ProcForm {...props} />;
    case 'vital':       return <VitalForm {...props} />;
    case 'twelve_lead': return <TwelveLeadForm {...props} />;
    case 'iv':          return <IvForm {...props} />;
    case 'note':        return <NoteForm {...props} />;
  }
}

function FormShell({ title, onCancel, children, onSubmit, busy }: {
  title: string; onCancel: () => void; children: React.ReactNode;
  onSubmit: () => void | Promise<void>; busy?: boolean;
}) {
  return (
    <form
      className="border border-[var(--cockpit-edge-deep)] rounded p-2 space-y-2 bg-black/30"
      onSubmit={(e) => { e.preventDefault(); void onSubmit(); }}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--cockpit-text-on-panel-2)]">
        {title} · auto-stamp now
      </div>
      {children}
      <div className="flex gap-1.5 pt-1">
        <button type="submit" disabled={busy} className="rail-button text-xs px-3 py-2 font-bold flex-1">
          {busy ? 'Logging…' : <><Plus size={14} className="inline mr-1" />Log now</>}
        </button>
        <button type="button" onClick={onCancel} className="rail-button no-bevel text-xs px-3 py-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

function MedForm(props: Parameters<typeof EntryForm>[0]) {
  const [drug_name, setDrug] = useState('');
  const [dose, setDose] = useState('');
  const [unit, setUnit] = useState('mg');
  const [route, setRoute] = useState('IV');
  const [response, setResponse] = useState('');
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!drug_name.trim()) { props.onError('Drug name required.'); return; }
    if (!props.userId) return;
    const scope = scopeBlockReason(props.certLevel, minScopeForDrug(drug_name));
    if (scope) { props.onError(`${drug_name}: ${scope}`); return; }
    setBusy(true);
    try {
      const id = crypto.randomUUID();
      const ts = new Date().toISOString();
      const row: MedicationGiven = {
        id, epcr_id: props.epcr.id,
        drug_name: drug_name.trim(), dose, unit, route,
        timestamp: ts, performed_by: props.userId,
        patient_response: response || null,
        controlled_substance: false, waste_documented: false, waste_witness: null,
        protocol_id: props.protocolId, protocol_step_order: null,
        is_deviation: false, deviation_reason: null,
        created_at: ts,
      };
      await db.medications_given.add(withSyncMeta(row));
      enqueueSync('medications_given', id, 'insert', row as unknown as Record<string, unknown>);
      props.onSubmit({ id, kind: 'med', timestamp: ts, label: `${drug_name} ${dose} ${unit} ${route}`, detail: response || undefined });
    } finally { setBusy(false); }
  }

  return (
    <FormShell title="Medication" onCancel={props.onCancel} onSubmit={go} busy={busy}>
      <input className="input-field" placeholder="Drug (e.g. Aspirin)" value={drug_name} onChange={(e) => setDrug(e.target.value)} autoFocus />
      <div className="grid grid-cols-3 gap-1.5">
        <input className="input-field" placeholder="Dose" value={dose} onChange={(e) => setDose(e.target.value)} />
        <select className="input-field" value={unit} onChange={(e) => setUnit(e.target.value)}>
          <option>mg</option><option>mcg</option><option>g</option><option>mL</option><option>units</option>
        </select>
        <select className="input-field" value={route} onChange={(e) => setRoute(e.target.value)}>
          <option>IV</option><option>IO</option><option>IM</option><option>PO</option><option>SL</option><option>SC</option><option>IN</option><option>NEB</option>
        </select>
      </div>
      <input className="input-field" placeholder="Response (optional)" value={response} onChange={(e) => setResponse(e.target.value)} />
    </FormShell>
  );
}

function ProcForm(props: Parameters<typeof EntryForm>[0]) {
  const [procedure_type, setProc] = useState('');
  const [body_site, setSite] = useState('');
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!procedure_type.trim()) { props.onError('Procedure required.'); return; }
    if (!props.userId) return;
    const scope = scopeBlockReason(props.certLevel, minScopeForProcedure(procedure_type));
    if (scope) { props.onError(`${procedure_type}: ${scope}`); return; }
    setBusy(true);
    try {
      const id = crypto.randomUUID();
      const ts = new Date().toISOString();
      const row: Procedure = {
        id, epcr_id: props.epcr.id, timestamp: ts,
        procedure_type: procedure_type.trim(),
        body_site: body_site.trim() || null,
        performed_by: props.userId, attempt_number: 1,
        outcome: 'successful', complications: null,
        protocol_id: props.protocolId, protocol_step_order: null,
        is_deviation: false, deviation_reason: null,
        created_at: ts,
      };
      await db.procedures.add(withSyncMeta(row));
      enqueueSync('procedures', id, 'insert', row as unknown as Record<string, unknown>);
      props.onSubmit({ id, kind: 'procedure', timestamp: ts, label: procedure_type, detail: body_site || undefined });
    } finally { setBusy(false); }
  }

  return (
    <FormShell title="Procedure" onCancel={props.onCancel} onSubmit={go} busy={busy}>
      <input className="input-field" placeholder="Procedure (e.g. BVM, Splinting)" value={procedure_type} onChange={(e) => setProc(e.target.value)} autoFocus />
      <input className="input-field" placeholder="Body site (optional)" value={body_site} onChange={(e) => setSite(e.target.value)} />
    </FormShell>
  );
}

function VitalForm(props: Parameters<typeof EntryForm>[0]) {
  const [bp_systolic, setSbp] = useState('');
  const [bp_diastolic, setDbp] = useState('');
  const [heart_rate, setHr] = useState('');
  const [respiratory_rate, setRr] = useState('');
  const [spo2, setSpo2] = useState('');
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      const id = crypto.randomUUID();
      const ts = new Date().toISOString();
      const row: VitalSet = {
        id, epcr_id: props.epcr.id, timestamp: ts, source: 'manual',
        bp_systolic: bp_systolic ? Number(bp_systolic) : null,
        bp_diastolic: bp_diastolic ? Number(bp_diastolic) : null,
        heart_rate: heart_rate ? Number(heart_rate) : null,
        heart_rhythm: null,
        respiratory_rate: respiratory_rate ? Number(respiratory_rate) : null,
        spo2: spo2 ? Number(spo2) : null,
        etco2: null,
        gcs_total: null, gcs_eye: null, gcs_verbal: null, gcs_motor: null,
        temperature: null, blood_glucose: null, pain_scale: null,
        created_at: ts,
      };
      await db.vital_sets.add(withSyncMeta(row));
      enqueueSync('vital_sets', id, 'insert', row as unknown as Record<string, unknown>);
      props.onSubmit({ id, kind: 'vital', timestamp: ts, label: 'Vital set', detail: vitalSummary(row) });
    } finally { setBusy(false); }
  }

  return (
    <FormShell title="Vital set" onCancel={props.onCancel} onSubmit={go} busy={busy}>
      <div className="grid grid-cols-2 gap-1.5">
        <input className="input-field" inputMode="numeric" placeholder="SBP"  value={bp_systolic}  onChange={(e) => setSbp(e.target.value)} autoFocus />
        <input className="input-field" inputMode="numeric" placeholder="DBP"  value={bp_diastolic} onChange={(e) => setDbp(e.target.value)} />
        <input className="input-field" inputMode="numeric" placeholder="HR"   value={heart_rate} onChange={(e) => setHr(e.target.value)} />
        <input className="input-field" inputMode="numeric" placeholder="RR"   value={respiratory_rate} onChange={(e) => setRr(e.target.value)} />
        <input className="input-field" inputMode="numeric" placeholder="SpO2" value={spo2} onChange={(e) => setSpo2(e.target.value)} />
      </div>
    </FormShell>
  );
}

function TwelveLeadForm(props: Parameters<typeof EntryForm>[0]) {
  const [interp, setInterp] = useState<'STEMI'|'NSTEMI'|'inconclusive'|'normal'>('normal');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!props.userId) return;
    setBusy(true);
    try {
      const id = crypto.randomUUID();
      const ts = new Date().toISOString();
      // Procedure record
      const proc: Procedure = {
        id, epcr_id: props.epcr.id, timestamp: ts,
        procedure_type: '12-lead ECG', body_site: null,
        performed_by: props.userId, attempt_number: 1,
        outcome: 'successful', complications: null,
        protocol_id: props.protocolId, protocol_step_order: null,
        is_deviation: false, deviation_reason: null,
        created_at: ts,
      };
      await db.procedures.add(withSyncMeta(proc));
      enqueueSync('procedures', id, 'insert', proc as unknown as Record<string, unknown>);
      // Patch ePCR.set_findings.stemi so cascade engine + alerts pick it up
      props.onUpdate({
        set_findings: {
          ...(props.epcr.set_findings ?? {}),
          stemi: {
            ...(props.epcr.set_findings?.stemi ?? {}),
            interpretation: interp,
            notes: notes || undefined,
            first_12_lead_time: ts,
          },
        },
      });
      props.onSubmit({ id, kind: 'twelve_lead', timestamp: ts, label: `12-lead: ${interp}`, detail: notes || undefined });
    } finally { setBusy(false); }
  }

  return (
    <FormShell title="12-lead ECG" onCancel={props.onCancel} onSubmit={go} busy={busy}>
      <select className="input-field" value={interp} onChange={(e) => setInterp(e.target.value as typeof interp)} autoFocus>
        <option value="normal">Normal</option>
        <option value="inconclusive">Inconclusive</option>
        <option value="STEMI">STEMI</option>
        <option value="NSTEMI">NSTEMI</option>
      </select>
      <textarea className="input-field" rows={2} placeholder="Findings (e.g. ST-elevation in II, III, aVF)" value={notes} onChange={(e) => setNotes(e.target.value)} />
    </FormShell>
  );
}

function IvForm(props: Parameters<typeof EntryForm>[0]) {
  const [size, setSize] = useState('18');
  const [site, setSite] = useState('Left AC');
  const [type, setType] = useState<'IV'|'IO'>('IV');
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!props.userId) return;
    const scope = scopeBlockReason(props.certLevel, minScopeForProcedure(type === 'IO' ? 'IO Access' : 'Peripheral IV'));
    if (scope) { props.onError(`${type} access: ${scope}`); return; }
    setBusy(true);
    try {
      const id = crypto.randomUUID();
      const ts = new Date().toISOString();
      const label = `${type} access · ${size}g · ${site}`;
      const proc: Procedure = {
        id, epcr_id: props.epcr.id, timestamp: ts,
        procedure_type: type === 'IO' ? 'IO Access' : 'Peripheral IV',
        body_site: `${size}g ${site}`,
        performed_by: props.userId, attempt_number: 1,
        outcome: 'successful', complications: null,
        protocol_id: props.protocolId, protocol_step_order: null,
        is_deviation: false, deviation_reason: null,
        created_at: ts,
      };
      await db.procedures.add(withSyncMeta(proc));
      enqueueSync('procedures', id, 'insert', proc as unknown as Record<string, unknown>);
      props.onSubmit({ id, kind: 'iv', timestamp: ts, label });
    } finally { setBusy(false); }
  }

  return (
    <FormShell title="IV / IO access" onCancel={props.onCancel} onSubmit={go} busy={busy}>
      <div className="grid grid-cols-3 gap-1.5">
        <select className="input-field" value={type} onChange={(e) => setType(e.target.value as 'IV'|'IO')}>
          <option>IV</option><option>IO</option>
        </select>
        <select className="input-field" value={size} onChange={(e) => setSize(e.target.value)}>
          <option>14</option><option>16</option><option>18</option><option>20</option><option>22</option><option>24</option>
        </select>
        <input className="input-field" placeholder="Site" value={site} onChange={(e) => setSite(e.target.value)} />
      </div>
    </FormShell>
  );
}

function NoteForm(props: Parameters<typeof EntryForm>[0]) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  async function go() {
    if (!text.trim()) { props.onError('Note text required.'); return; }
    setBusy(true);
    try {
      const id = crypto.randomUUID();
      const ts = new Date().toISOString();
      // Notes don't have a Dexie table yet — store under
      // ePCR.set_findings.contact_notes so they're persisted with
      // the record. Future commit can promote to a typed table.
      const cur = (props.epcr.set_findings?.contact_notes as { entries?: Array<{ ts: string; text: string }> } | undefined);
      const entries = cur?.entries ?? [];
      entries.push({ ts, text: text.trim() });
      props.onUpdate({
        set_findings: {
          ...(props.epcr.set_findings ?? {}),
          contact_notes: { entries },
        },
      });
      props.onSubmit({ id, kind: 'note', timestamp: ts, label: 'Note', detail: text.trim() });
    } finally { setBusy(false); }
  }

  return (
    <FormShell title="Note" onCancel={props.onCancel} onSubmit={go} busy={busy}>
      <textarea className="input-field" rows={3} placeholder="Quick observation, patient quote, scene detail…" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
    </FormShell>
  );
}

/* ================================================================
 * Event log row
 * ================================================================ */

const KIND_ACCENT: Record<EntryKind, string> = {
  med:         'border-l-amber-500',
  procedure:   'border-l-violet-500',
  vital:       'border-l-emerald-500',
  twelve_lead: 'border-l-rose-500',
  iv:          'border-l-blue-500',
  note:        'border-l-slate-400',
};

const KIND_ICON: Record<EntryKind, typeof Pill> = {
  med:         Pill,
  procedure:   Stethoscope,
  vital:       Activity,
  twelve_lead: Heart,
  iv:          Droplet,
  note:        FileText,
};

function LogRowDisplay({
  row, onUndo, onEditTime, locked,
}: {
  row: LogRow;
  onUndo: () => void;
  onEditTime: () => void;
  locked: boolean;
}) {
  const Icon = KIND_ICON[row.kind];
  return (
    <li className={cn(
      'border-l-4 bg-black/30 rounded px-2 py-1.5 flex items-start gap-2',
      KIND_ACCENT[row.kind],
    )}>
      <Icon size={14} className="text-[var(--cockpit-text-on-panel-2)] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-mono text-[12px] text-emerald-100 tabular-nums font-bold">
            {new Date(row.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
          </span>
          <span className="text-[13px] font-semibold text-[var(--cockpit-text-on-panel)]">{row.label}</span>
        </div>
        {row.detail && (
          <div className="text-[11px] text-[var(--cockpit-text-on-panel-2)] mt-0.5">{row.detail}</div>
        )}
      </div>
      {!locked && row.kind !== 'note' && (
        <button
          type="button"
          onClick={onEditTime}
          className="rail-button-mini text-sky-300 hover:text-sky-100 shrink-0 self-center"
          title="Correct this timestamp"
          aria-label="Edit timestamp"
        >
          <Edit3 size={12} />
        </button>
      )}
      {!locked && (
        <button
          type="button"
          onClick={onUndo}
          className="rail-button-mini text-amber-300 hover:text-amber-100 shrink-0 self-center"
          title="Undo this entry — deletes the structured record"
          aria-label="Undo entry"
        >
          <Undo2 size={12} />
        </button>
      )}
    </li>
  );
}

function vitalSummary(v: VitalSet): string {
  const parts: string[] = [];
  if (v.bp_systolic && v.bp_diastolic) parts.push(`BP ${v.bp_systolic}/${v.bp_diastolic}`);
  if (v.heart_rate) parts.push(`HR ${v.heart_rate}`);
  if (v.respiratory_rate) parts.push(`RR ${v.respiratory_rate}`);
  if (v.spo2) parts.push(`SpO2 ${v.spo2}%`);
  return parts.join(' · ') || '—';
}
